/**
 * Gemini2 product-card hover video (facade).
 *
 * Works with the deferred markup in snippets/product-grid-item-gemini2.liquid:
 *   <video class="media-video" preload="none" data-hover-video>
 *     <source data-src="..." type="video/mp4">
 *   </video>
 *
 * Cards render a poster image only. The video's sources stay in data-src and load
 * + play only on real intent:
 *   - Desktop (hover-capable): on mouseenter, pause/unload on mouseleave.
 *   - Touch / no-hover: when the card video is substantially visible (or on tap).
 * Only one card video is kept loaded at a time. Delegated + MutationObserver based,
 * so it also covers cards injected later by the recommendation/recently-viewed APIs.
 */
(function () {
  'use strict';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isSaveDataOrSlow() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData) return true;
    var t = (c.effectiveType || '').toLowerCase();
    return t === '2g' || t === 'slow-2g';
  }

  var AUTO_OK = !prefersReducedMotion() && !isSaveDataOrSlow();
  var CAN_HOVER = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function loadSources(video) {
    if (!video || video.dataset.sourcesLoaded === 'true') return;
    var sources = video.querySelectorAll('source[data-src]');
    if (!sources.length) return;
    sources.forEach(function (s) {
      var url = s.getAttribute('data-src');
      if (url) {
        s.setAttribute('src', url);
        s.removeAttribute('data-src');
      }
    });
    video.load();
    video.dataset.sourcesLoaded = 'true';
  }

  function unloadSources(video) {
    if (!video || video.dataset.sourcesLoaded !== 'true') return;
    safePause(video);
    video.querySelectorAll('source[src]').forEach(function (s) {
      var url = s.getAttribute('src');
      if (url && !s.getAttribute('data-src')) s.setAttribute('data-src', url);
      s.removeAttribute('src');
    });
    video.removeAttribute('src');
    delete video.dataset.sourcesLoaded;
    video.load();
  }

  // Track the pending play() promise so we never pause() before it settles
  // (avoids "The play() request was interrupted by a call to pause()" AbortError).
  function safePlay(video) {
    loadSources(video);
    var p = video.play();
    if (p && typeof p.then === 'function') {
      video._playPromise = p;
      var clear = function () { if (video._playPromise === p) video._playPromise = null; };
      p.then(clear, clear);
    }
  }

  function safePause(video) {
    if (video._playPromise) {
      video._playPromise.then(function () { try { video.pause(); } catch (e) {} });
    } else {
      try { video.pause(); } catch (e) {}
    }
  }

  // Keep at most one card video loaded/playing at a time.
  var current = null;
  function activate(video) {
    if (current && current !== video) {
      var prev = current;
      current = null;
      unloadSources(prev);
    }
    current = video;
    safePlay(video);
  }
  function deactivate(video) {
    if (current === video) current = null;
    unloadSources(video);
  }

  if (CAN_HOVER) {
    document.addEventListener('mouseover', function (e) {
      var card = e.target.closest && e.target.closest('[data-product-image]');
      if (!card) return;
      var video = card.querySelector('[data-hover-video]');
      // offsetParent null = the slide is display:none (e.g. video-first cards on
      // desktop, where the main autoplay video plays instead) -- don't download it.
      if (video && video !== current && AUTO_OK && video.offsetParent !== null) activate(video);
    });
    document.addEventListener('mouseout', function (e) {
      var card = e.target.closest && e.target.closest('[data-product-image]');
      if (!card) return;
      // Ignore moves between elements inside the same card.
      if (e.relatedTarget && card.contains(e.relatedTarget)) return;
      var video = card.querySelector('[data-hover-video]');
      if (video) deactivate(video);
    });
  }

  var io = null;
  if (!CAN_HOVER && 'IntersectionObserver' in window) {
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          if (AUTO_OK) activate(video);
        } else if (video.dataset.sourcesLoaded === 'true') {
          deactivate(video);
        }
      });
    }, { threshold: [0, 0.6, 1] });
  }

  // Main-media autoplay videos ([data-card-main-video]): on desktop, cards whose
  // main media is a video autoplay it in place of the static poster. Loaded and
  // played only when near the viewport; paused (kept loaded) when scrolled away.
  // These are NOT part of the one-at-a-time hover budget -- several featured cards
  // can play at once, which is the intended design. Hidden on touch via CSS, so
  // only observe on hover-capable devices to avoid downloading invisible videos.
  var mainIo = null;
  if (CAN_HOVER && AUTO_OK && 'IntersectionObserver' in window) {
    mainIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        if (entry.isIntersecting) {
          safePlay(video);
        } else if (video.dataset.sourcesLoaded === 'true') {
          safePause(video);
        }
      });
    }, { threshold: 0.1, rootMargin: '100px 0px' });
  }

  function bind(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-hover-video]').forEach(function (video) {
      if (video.dataset.hoverBound === 'true') return;
      video.dataset.hoverBound = 'true';
      video.addEventListener('click', function () {
        if (video.paused) safePlay(video); else safePause(video);
      });
      if (io) io.observe(video);
    });
    if (mainIo) {
      scope.querySelectorAll('[data-card-main-video]').forEach(function (video) {
        if (video.dataset.mainBound === 'true') return;
        video.dataset.mainBound = 'true';
        mainIo.observe(video);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bind(document); });
  } else {
    bind(document);
  }

  document.addEventListener('shopify:section:load', function (e) { bind(e.target || document); });

  // Cards injected asynchronously (recommendations / recently viewed / quick add).
  if ('MutationObserver' in window) {
    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (!m.addedNodes) return;
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('[data-hover-video], [data-card-main-video]')) {
            bind(node.parentNode || document);
          } else if (node.querySelector && node.querySelector('[data-hover-video], [data-card-main-video]')) {
            bind(node);
          }
        });
      });
    });
    if (document.body) mo.observe(document.body, { childList: true, subtree: true });
  }
})();
